import Fuse from 'fuse.js'
import {
  ArrowLeft,
  Check,
  ExternalLink,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-hot-toast'
import { Link, useLocation, useParams } from '#router'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Textarea } from '@/components/ui/textarea'
import { AUTO_SAVE_DEBOUNCE_MS, useAutoSave } from '@/hooks/use-auto-save'
import { usePageTitle } from '@/hooks/use-document-title'
import { generateSlugFromName } from '@/lib/slug'
import { type RouterOutputs, trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { RESOURCE_TYPE_COLORS } from '../shared/constants'
import { EditResourceModal } from './edit-resource-modal'
import { useResources } from './hooks/use-resources'
import { SDAssertionsSection } from './sd-assertions-section'
import { type SDProblemSource, SDSourcesSection } from './sd-sources-section'

interface SystemDesignFormData {
  title: string
  description: string
  difficulty: string
  /** CompanyTag slugs — the curated vocabulary that replaced free-text company. */
  companyTagSlugs: string[]
  topics: string[]
  hints: string[]
  sampleSolution: string
  editorialMarkdown: string
  interviewType: SystemDesignInterviewType | ''
  companyContext: SystemDesignCompanyContext | ''
  timeCap: number | null
  estimatedMinutes: number | null
}

/** A linked resource as the router returns it — derived, not re-declared. */
type LinkedResource =
  RouterOutputs['admin']['content']['getSystemDesignProblem']['resources'][number]

const RESOURCE_TYPE_OPTIONS = ['video', 'article', 'course', 'book', 'tool']

const DIFFICULTY_OPTIONS = [
  { value: '', label: 'No difficulty' },
  { value: 'Easy', label: 'Easy' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Hard', label: 'Hard' },
]

// Both enums are open by design (LG-922 decision 2) — expect values to be added.
type SystemDesignInterviewType = 'product' | 'infrastructure'
type SystemDesignCompanyContext = 'startup' | 'large_scale'

const INTERVIEW_TYPE_OPTIONS: {
  value: SystemDesignInterviewType | ''
  label: string
}[] = [
  { value: '', label: 'Not set' },
  { value: 'product', label: 'Product' },
  { value: 'infrastructure', label: 'Infrastructure' },
]

const COMPANY_CONTEXT_OPTIONS: {
  value: SystemDesignCompanyContext | ''
  label: string
}[] = [
  { value: '', label: 'Not set' },
  { value: 'startup', label: 'Startup' },
  { value: 'large_scale', label: 'Large scale' },
]

/** Shared by every native `<select>` here, matching the Input component's chrome. */
const SELECT_CLASS = cn(
  'border-input bg-background h-9 w-full rounded-md border px-3 text-sm shadow-xs',
  'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
  'outline-none transition-[color,box-shadow]',
  'dark:bg-input/30',
)

function getDefaultFormData(): SystemDesignFormData {
  return {
    title: '',
    description: '',
    difficulty: '',
    companyTagSlugs: [],
    topics: [],
    hints: [],
    sampleSolution: '',
    editorialMarkdown: '',
    interviewType: '',
    companyContext: '',
    timeCap: null,
    estimatedMinutes: null,
  }
}

export function SystemDesignEditor() {
  const params = useParams()
  const location = useLocation()
  const { navigate } = location

  const isNew = params.id === 'new'
  const [problemId, setProblemId] = useState<string | null>(
    isNew ? null : (params.id ?? null),
  )

  const [formData, setFormData] =
    useState<SystemDesignFormData>(getDefaultFormData)
  const [slug, setSlug] = useState<string>('')

  // Track whether we have created the problem (for "new" mode)
  const problemIdRef = useRef(problemId)
  problemIdRef.current = problemId

  // Topics input state
  const [topicsInput, setTopicsInput] = useState('')
  const [topicsInputFocused, setTopicsInputFocused] = useState(false)

  // Stable IDs for hint elements (to satisfy lint rules about array index keys)
  const hintIdCounterRef = useRef(0)
  const [hintKeys, setHintKeys] = useState<string[]>([])

  // Drag state for hints reordering
  const [dragIndex, setDragIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  // Sources state
  const [linkedSources, setLinkedSources] = useState<SDProblemSource[]>([])

  // Resources state
  const [linkedResources, setLinkedResources] = useState<LinkedResource[]>([])
  const {
    resources: allResources,
    loading: resourcesLoading,
    createResource,
  } = useResources()
  const [showAddResources, setShowAddResources] = useState(false)
  const [resourceSearchQuery, setResourceSearchQuery] = useState('')
  const [selectedResourceIds, setSelectedResourceIds] = useState<Set<string>>(
    () => new Set(),
  )
  const [isAddingResources, setIsAddingResources] = useState(false)
  const [showCreateResource, setShowCreateResource] = useState(false)
  const [newResourceTitle, setNewResourceTitle] = useState('')
  const [newResourceUrl, setNewResourceUrl] = useState('')
  const [newResourceType, setNewResourceType] = useState('article')
  const [isCreatingResource, setIsCreatingResource] = useState(false)
  const [editingResource, setEditingResource] = useState<LinkedResource | null>(
    null,
  )
  // Drag state for resource reordering
  const [resourceDragIndex, setResourceDragIndex] = useState<number | null>(
    null,
  )
  const [resourceDragOverIndex, setResourceDragOverIndex] = useState<
    number | null
  >(null)

  usePageTitle(
    isNew && !formData.title
      ? 'New System Design Problem'
      : formData.title || null,
  )

  // tRPC query for loading problem data
  const utils = trpc.useUtils()
  const {
    data: problemData,
    isLoading: loading,
    error: loadError,
  } = trpc.admin.content.getSystemDesignProblem.useQuery(
    { id: problemId! },
    { enabled: !!problemId && !isNew },
  )

  // All distinct topics across system design problems (for autocomplete)
  const { data: allTopics = [] } =
    trpc.admin.content.getSystemDesignTopics.useQuery()

  // The curated company-tag vocabulary, shared with the companies admin
  const { data: allCompanyTags = [] } = trpc.admin.companyTags.list.useQuery()

  // tRPC mutations
  const createMutation =
    trpc.admin.content.createSystemDesignProblem.useMutation()
  const updateMutation =
    trpc.admin.content.updateSystemDesignProblem.useMutation()
  const addResourcesMutation =
    trpc.admin.content.addSystemDesignProblemResources.useMutation()
  const removeResourcesMutation =
    trpc.admin.content.removeSystemDesignProblemResources.useMutation()
  const reorderResourcesMutation =
    trpc.admin.content.reorderSystemDesignProblemResources.useMutation()

  // Declared ahead of `onSave`, which seeds it for a just-created problem.
  // Hydrate the form once per loaded problem. `problemData` is a fresh object on
  // every refetch — three call sites fetch explicitly and refetchOnWindowFocus
  // fires past the 30s staleTime — and rehydrating there would overwrite
  // in-progress edits. Worse than a visual revert: updateField calls
  // scheduleSave() bare, so useAutoSave reads formData at fire time and the
  // reverted text would be persisted. Every explicit refetch already applies
  // its own result, so nothing depends on this effect re-running.
  const hydratedProblemIdRef = useRef<string | null>(null)
  useEffect(() => {
    if (!problemData) return
    if (hydratedProblemIdRef.current === problemData.id) return
    hydratedProblemIdRef.current = problemData.id
    const hints = problemData.hints || []
    setFormData({
      title: problemData.title,
      description: problemData.description || '',
      difficulty: problemData.difficulty?.name || '',
      companyTagSlugs: problemData.companyTags.map((t) => t.slug),
      topics: problemData.topics || [],
      hints,
      sampleSolution: problemData.sampleSolution || '',
      editorialMarkdown: problemData.editorialMarkdown || '',
      interviewType: problemData.interviewType ?? '',
      companyContext: problemData.companyContext ?? '',
      timeCap: problemData.timeCap,
      estimatedMinutes: problemData.estimatedMinutes,
    })
    // Generate stable keys for loaded hints
    const keys = hints.map(() => {
      hintIdCounterRef.current += 1
      return `hint-${hintIdCounterRef.current}`
    })
    setHintKeys(keys)
    setSlug(problemData.slug)
    setLinkedSources(problemData.sources)
    setLinkedResources(problemData.resources)
  }, [problemData])

  // The id the server assigned to a just-created problem. Written inside onSave
  // the moment the create resolves — NOT in onSaved, which the hook skips for a
  // superseded save: the next queued save would then still see no id and create
  // the problem a second time.
  const createdIdRef = useRef<string | null>(null)
  // Slug owed to the UI. Cosmetic and idempotent, so it can ride onSaved.
  const savedSlugRef = useRef<string | null>(null)

  // Auto-save the whole form: the first save creates the problem, later ones
  // update it.
  const { state: saveStatus, onChange: scheduleSave } = useAutoSave({
    value: formData,
    debounceMs: AUTO_SAVE_DEBOUNCE_MS.fast,
    logLabel: 'System design problem',
    // An untitled problem is not persistable — keep collecting edits instead.
    canSave: (data) => data.title.trim().length > 0,
    onSave: async (data) => {
      const currentProblemId = problemIdRef.current ?? createdIdRef.current
      const body = {
        title: data.title,
        description: data.description || null,
        difficulty: data.difficulty || null,
        companyTagSlugs: data.companyTagSlugs,
        topics: data.topics,
        hints: data.hints,
        sampleSolution: data.sampleSolution || null,
        editorialMarkdown: data.editorialMarkdown || null,
        interviewType: data.interviewType || null,
        companyContext: data.companyContext || null,
        timeCap: data.timeCap,
        estimatedMinutes: data.estimatedMinutes,
      }

      const isCreating = !currentProblemId
      const savedData = isCreating
        ? await createMutation.mutateAsync(body)
        : await updateMutation.mutateAsync({ id: currentProblemId, ...body })

      if (isCreating) {
        // Adopting the id and leaving /new are both exactly-once side effects
        // of this create response, so they belong here and not in onSaved.
        // onSaved is skipped for a superseded save, and canSave can hold that
        // skip open indefinitely — clear the title while the create is in
        // flight and nothing is sendable again — which left the record created
        // on the server with the editor still sitting on /new. createdIdRef is
        // in-memory only, so a reload from there created the problem twice.
        createdIdRef.current = savedData.id
        // Also mark this id hydrated. `isNew` comes from live router params, so
        // the navigate below flips it false, which enables the fetch — and the
        // hydrate guard is still null, because it is only ever written from
        // `problemData`. The response then lands as a "first" hydration and
        // overwrites the whole form with the server's snapshot as of this
        // create, silently reverting anything typed during the debounce +
        // create + fetch window. Local state is authoritative for a problem
        // this session just created.
        hydratedProblemIdRef.current = savedData.id
        setProblemId(savedData.id)
        navigate(`/admin/system-design/${savedData.id}`, { replace: true })
      }
      savedSlugRef.current = savedData.slug
    },
    onSaved: () => {
      if (savedSlugRef.current !== null) {
        setSlug(savedSlugRef.current)
      }
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Failed to save')
    },
  })

  // Update form data and schedule a save
  const updateField = useCallback(
    <K extends keyof SystemDesignFormData>(
      field: K,
      value: SystemDesignFormData[K],
    ) => {
      setFormData((prev) => ({ ...prev, [field]: value }))

      // Auto-generate slug from title on the client side for preview
      if (field === 'title' && typeof value === 'string') {
        setSlug(generateSlugFromName(value))
      }

      scheduleSave()
    },
    [scheduleSave],
  )

  // Topic management
  const addTopic = useCallback(
    (topic: string) => {
      const trimmed = topic.trim()
      if (!trimmed) return
      setFormData((prev) => {
        if (prev.topics.includes(trimmed)) return prev
        return { ...prev, topics: [...prev.topics, trimmed] }
      })
      scheduleSave()
    },
    [scheduleSave],
  )

  const removeTopic = useCallback(
    (topic: string) => {
      setFormData((prev) => ({
        ...prev,
        topics: prev.topics.filter((t) => t !== topic),
      }))
      scheduleSave()
    },
    [scheduleSave],
  )

  // Company tags — the same add/remove shape as topics, over a curated
  // vocabulary rather than free text.
  const addCompanyTag = useCallback(
    (slug: string) => {
      setFormData((prev) => {
        if (prev.companyTagSlugs.includes(slug)) return prev
        return { ...prev, companyTagSlugs: [...prev.companyTagSlugs, slug] }
      })
      scheduleSave()
    },
    [scheduleSave],
  )

  const removeCompanyTag = useCallback(
    (slug: string) => {
      setFormData((prev) => ({
        ...prev,
        companyTagSlugs: prev.companyTagSlugs.filter((s) => s !== slug),
      }))
      scheduleSave()
    },
    [scheduleSave],
  )

  // Selected tags in the vocabulary's own order, so the chips do not reshuffle
  // as they are added and removed. A slug with no matching tag row — a tag
  // retired since the problem was authored — is dropped rather than rendered
  // as a blank chip.
  const selectedCompanyTags = useMemo(
    () =>
      allCompanyTags.filter((tag) =>
        formData.companyTagSlugs.includes(tag.slug),
      ),
    [allCompanyTags, formData.companyTagSlugs],
  )

  const availableCompanyTags = useMemo(
    () =>
      allCompanyTags.filter(
        (tag) => !formData.companyTagSlugs.includes(tag.slug),
      ),
    [allCompanyTags, formData.companyTagSlugs],
  )

  // Topics filtered for autocomplete dropdown
  const filteredTopics = useMemo(() => {
    const available = allTopics.filter((t) => !formData.topics.includes(t))
    if (!topicsInput.trim()) return available
    const lower = topicsInput.toLowerCase()
    return available.filter((t) => t.toLowerCase().includes(lower))
  }, [allTopics, formData.topics, topicsInput])

  const showAddNewTopic = useMemo(() => {
    const trimmed = topicsInput.trim()
    if (!trimmed) return false
    const lower = trimmed.toLowerCase()
    if (formData.topics.some((t) => t.toLowerCase() === lower)) return false
    if (filteredTopics.some((t) => t.toLowerCase() === lower)) return false
    return true
  }, [topicsInput, formData.topics, filteredTopics])

  const hasTopicSuggestions = filteredTopics.length > 0 || showAddNewTopic

  const handleTopicsKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' || e.key === ',') {
        e.preventDefault()
        const value = topicsInput.trim().replace(/,$/g, '')
        if (value) {
          // Support comma-separated input
          const parts = value
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean)
          for (const part of parts) {
            addTopic(part)
          }
          setTopicsInput('')
        }
      }
    },
    [topicsInput, addTopic],
  )

  const handleTopicsBlur = useCallback(() => {
    const value = topicsInput.trim().replace(/,$/g, '')
    if (value) {
      const parts = value
        .split(',')
        .map((p) => p.trim())
        .filter(Boolean)
      for (const part of parts) {
        addTopic(part)
      }
      setTopicsInput('')
    }
    setTopicsInputFocused(false)
  }, [topicsInput, addTopic])

  // Hint management
  const addHint = useCallback(() => {
    hintIdCounterRef.current += 1
    const newKey = `hint-${hintIdCounterRef.current}`
    setFormData((prev) => ({
      ...prev,
      hints: [...prev.hints, ''],
    }))
    setHintKeys((prev) => [...prev, newKey])
  }, [])

  const updateHint = useCallback(
    (index: number, value: string) => {
      setFormData((prev) => {
        const newHints = [...prev.hints]
        newHints[index] = value
        return { ...prev, hints: newHints }
      })
      scheduleSave()
    },
    [scheduleSave],
  )

  const removeHint = useCallback(
    (index: number) => {
      setFormData((prev) => ({
        ...prev,
        hints: prev.hints.filter((_, i) => i !== index),
      }))
      setHintKeys((prev) => prev.filter((_, i) => i !== index))
      scheduleSave()
    },
    [scheduleSave],
  )

  // Drag-and-drop reorder for hints
  const handleHintDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      setDragIndex(index)
      e.dataTransfer.effectAllowed = 'move'
    },
    [],
  )

  const handleHintDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setDragOverIndex(index)
    },
    [],
  )

  const handleHintDrop = useCallback(
    (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault()
      if (dragIndex === null || dragIndex === dropIndex) {
        setDragIndex(null)
        setDragOverIndex(null)
        return
      }

      setFormData((prev) => {
        const newHints = [...prev.hints]
        const [removed] = newHints.splice(dragIndex, 1)
        newHints.splice(dropIndex, 0, removed)
        return { ...prev, hints: newHints }
      })
      setHintKeys((prev) => {
        const newKeys = [...prev]
        const [removed] = newKeys.splice(dragIndex, 1)
        newKeys.splice(dropIndex, 0, removed)
        return newKeys
      })
      setDragIndex(null)
      setDragOverIndex(null)
      scheduleSave()
    },
    [dragIndex, scheduleSave],
  )

  const handleHintDragEnd = useCallback(() => {
    setDragIndex(null)
    setDragOverIndex(null)
  }, [])

  // --- Resource management ---

  // Resources available to link (not already linked)
  const availableResources = useMemo(
    () =>
      allResources.filter((r) => !linkedResources.some((lr) => lr.id === r.id)),
    [allResources, linkedResources],
  )

  const fuse = useMemo(
    () =>
      new Fuse(availableResources, {
        keys: ['title', 'url'],
        threshold: 0.4,
      }),
    [availableResources],
  )

  const filteredResources = useMemo(() => {
    if (!resourceSearchQuery.trim()) return []
    return fuse.search(resourceSearchQuery).map((r) => r.item)
  }, [fuse, resourceSearchQuery])

  const handleToggleResource = useCallback((id: string) => {
    setSelectedResourceIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleAddSelectedResources = useCallback(async () => {
    if (selectedResourceIds.size === 0 || !problemId) return
    setIsAddingResources(true)
    try {
      await addResourcesMutation.mutateAsync({
        problemId,
        resourceIds: Array.from(selectedResourceIds),
      })
      // Invalidate and refetch problem data to get updated resources
      const updatedData =
        await utils.admin.content.getSystemDesignProblem.fetch({
          id: problemId,
        })
      setLinkedResources(updatedData.resources)
      setSelectedResourceIds(new Set())
      setResourceSearchQuery('')
      setShowAddResources(false)
      toast.success(`Added ${selectedResourceIds.size} resource(s)`)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to add resources',
      )
    } finally {
      setIsAddingResources(false)
    }
  }, [selectedResourceIds, problemId, addResourcesMutation, utils])

  const handleRemoveResource = useCallback(
    async (resourceId: string) => {
      if (!problemId) return
      try {
        await removeResourcesMutation.mutateAsync({
          problemId,
          resourceIds: [resourceId],
        })
        setLinkedResources((prev) => prev.filter((r) => r.id !== resourceId))
        toast.success('Resource removed')
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : 'Failed to remove resource',
        )
      }
    },
    [problemId, removeResourcesMutation],
  )

  const handleResourceUpdated = useCallback(
    (updated: {
      id: string
      title: string
      url: string
      type: string
      description: string | null
      icon: string | null
    }) => {
      setLinkedResources((prev) =>
        prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)),
      )
    },
    [],
  )

  const handleCreateAndLinkResource = useCallback(async () => {
    if (!newResourceTitle.trim() || !newResourceUrl.trim() || !problemId) return
    setIsCreatingResource(true)
    try {
      const created = await createResource({
        title: newResourceTitle.trim(),
        url: newResourceUrl.trim(),
        type: newResourceType,
      })
      // Link it to this problem
      await addResourcesMutation.mutateAsync({
        problemId,
        resourceIds: [created.id],
      })
      // Refetch to get updated resources
      const updatedData =
        await utils.admin.content.getSystemDesignProblem.fetch({
          id: problemId,
        })
      setLinkedResources(updatedData.resources)
      setNewResourceTitle('')
      setNewResourceUrl('')
      setNewResourceType('article')
      setShowCreateResource(false)
      toast.success('Resource created and linked')
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to create resource',
      )
    } finally {
      setIsCreatingResource(false)
    }
  }, [
    newResourceTitle,
    newResourceUrl,
    newResourceType,
    problemId,
    createResource,
    addResourcesMutation,
    utils,
  ])

  // Resource drag-and-drop reorder
  const handleResourceDragStart = useCallback(
    (e: React.DragEvent, index: number) => {
      setResourceDragIndex(index)
      e.dataTransfer.effectAllowed = 'move'
    },
    [],
  )

  const handleResourceDragOver = useCallback(
    (e: React.DragEvent, index: number) => {
      e.preventDefault()
      e.dataTransfer.dropEffect = 'move'
      setResourceDragOverIndex(index)
    },
    [],
  )

  const handleResourceDrop = useCallback(
    async (e: React.DragEvent, dropIndex: number) => {
      e.preventDefault()
      if (
        resourceDragIndex === null ||
        resourceDragIndex === dropIndex ||
        !problemId
      ) {
        setResourceDragIndex(null)
        setResourceDragOverIndex(null)
        return
      }

      const newResources = [...linkedResources]
      const [removed] = newResources.splice(resourceDragIndex, 1)
      newResources.splice(dropIndex, 0, removed)
      // Update order values
      const reordered = newResources.map((r, i) => ({ ...r, order: i }))
      setLinkedResources(reordered)
      setResourceDragIndex(null)
      setResourceDragOverIndex(null)

      // Persist the new order
      try {
        await reorderResourcesMutation.mutateAsync({
          problemId,
          resourceIds: reordered.map((r) => r.id),
        })
      } catch {
        // Revert on error
        toast.error('Failed to reorder resources')
        // Refetch to get correct order
        const updatedData =
          await utils.admin.content.getSystemDesignProblem.fetch({
            id: problemId,
          })
        setLinkedResources(updatedData.resources)
      }
    },
    [
      resourceDragIndex,
      linkedResources,
      problemId,
      reorderResourcesMutation,
      utils,
    ],
  )

  const handleResourceDragEnd = useCallback(() => {
    setResourceDragIndex(null)
    setResourceDragOverIndex(null)
  }, [])

  const error = loadError?.message ?? null

  // Loading state
  if (loading && !isNew) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    )
  }

  // Error state
  if (error && !isNew) {
    return (
      <div className="container mx-auto max-w-6xl px-4 py-8">
        <div className="bg-destructive/10 border-destructive/30 text-destructive rounded-lg border p-4">
          {error}
        </div>
        <Button asChild className="mt-4" variant="outline">
          <Link href="/admin/system-design">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to System Design Problems
          </Link>
        </Button>
      </div>
    )
  }

  return (
    <div className="container mx-auto max-w-6xl px-4 py-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="sm">
            <Link href="/admin/system-design">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Link>
          </Button>
          <div className="text-muted-foreground text-sm">
            {isNew && !problemId
              ? 'New System Design Problem'
              : 'Edit System Design Problem'}
          </div>
        </div>

        {/* Save status indicator */}
        <div className="flex items-center gap-2">
          {saveStatus === 'saving' && (
            <div className="text-muted-foreground flex items-center gap-1.5 text-sm">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" />
              Saving...
            </div>
          )}
          {saveStatus === 'saved' && (
            <div className="flex items-center gap-1.5 text-sm text-green-600">
              <Check className="h-3.5 w-3.5" />
              Saved
            </div>
          )}
          {saveStatus === 'error' && (
            <div className="text-sm text-red-600">Save failed</div>
          )}
        </div>
      </div>

      {/* Title field - large prominent input */}
      <div className="mb-1">
        <Input
          aria-label="Problem title"
          value={formData.title}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Problem title (e.g. Design a URL Shortener)"
          className="h-12 border-none text-2xl font-bold shadow-none focus-visible:ring-0"
        />
      </div>

      {/* Slug */}
      <div className="text-muted-foreground mb-6 pl-3 font-mono text-sm">
        {slug || 'slug-will-appear-here'}
      </div>

      {/* Two-column layout */}
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* Left column - Description and Sample Solution */}
        <div className="space-y-6">
          {/* Description */}
          <div>
            <label
              htmlFor="sd-description"
              className="text-foreground mb-1.5 block text-sm font-medium"
            >
              Description
            </label>
            <Textarea
              id="sd-description"
              value={formData.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Describe the system design problem, constraints, and requirements..."
              rows={8}
              className="resize-y"
            />
          </div>

          {/* Sample Solution */}
          <div>
            <label
              htmlFor="sd-sample-solution"
              className="text-foreground mb-1.5 block text-sm font-medium"
            >
              Sample Solution
            </label>
            <Textarea
              id="sd-sample-solution"
              value={formData.sampleSolution}
              onChange={(e) => updateField('sampleSolution', e.target.value)}
              placeholder="Write the sample solution or key discussion points..."
              rows={16}
              className="resize-y font-mono text-sm"
            />
          </div>

          {/* Editorial */}
          <div>
            <label
              htmlFor="sd-editorial-markdown"
              className="text-foreground mb-1.5 block text-sm font-medium"
            >
              Editorial
            </label>
            <Textarea
              id="sd-editorial-markdown"
              value={formData.editorialMarkdown}
              onChange={(e) => updateField('editorialMarkdown', e.target.value)}
              placeholder="Markdown shown in the whiteboard page's editorial pane..."
              rows={16}
              className="resize-y font-mono text-sm"
            />
          </div>
        </div>

        {/* Right column - Metadata */}
        <div className="space-y-5">
          {/* Difficulty */}
          <div>
            <label
              htmlFor="sd-difficulty"
              className="text-foreground mb-1.5 block text-sm font-medium"
            >
              Difficulty
            </label>
            <select
              id="sd-difficulty"
              value={formData.difficulty}
              onChange={(e) => updateField('difficulty', e.target.value)}
              className={SELECT_CLASS}
            >
              {DIFFICULTY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Interview type */}
          <div>
            <label
              htmlFor="sd-interview-type"
              className="text-foreground mb-1.5 block text-sm font-medium"
            >
              Interview type
            </label>
            <select
              id="sd-interview-type"
              value={formData.interviewType}
              onChange={(e) =>
                updateField(
                  'interviewType',
                  e.target.value as SystemDesignInterviewType | '',
                )
              }
              className={SELECT_CLASS}
            >
              {INTERVIEW_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-muted-foreground mt-1 text-xs">
              Changes how the interviewer prompts and grades.
            </p>
          </div>

          {/* Company context */}
          <div>
            <label
              htmlFor="sd-company-context"
              className="text-foreground mb-1.5 block text-sm font-medium"
            >
              Company context
            </label>
            <select
              id="sd-company-context"
              value={formData.companyContext}
              onChange={(e) =>
                updateField(
                  'companyContext',
                  e.target.value as SystemDesignCompanyContext | '',
                )
              }
              className={SELECT_CLASS}
            >
              {COMPANY_CONTEXT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Time cap */}
          <div>
            <label
              htmlFor="sd-time-cap"
              className="text-foreground mb-1.5 block text-sm font-medium"
            >
              Time cap (minutes)
            </label>
            <Input
              id="sd-time-cap"
              type="number"
              min={1}
              value={formData.timeCap ?? ''}
              onChange={(e) =>
                updateField(
                  'timeCap',
                  e.target.value ? Number(e.target.value) : null,
                )
              }
              placeholder="e.g. 45"
            />
            <p className="text-muted-foreground mt-1 text-xs">
              Length of one whiteboard session. Separate from the catalog
              estimate below.
            </p>
          </div>

          {/* Company tags */}
          <div>
            {/* A heading, not a `label`: it names the chip group *and* the
                select below it, so it has no single control to point at. The
                select carries its own `aria-label` instead — same form as the
                toggles in `sd-sources-section`. */}
            <p className="text-foreground mb-1.5 block text-sm font-medium">
              Company tags
            </p>
            <div className="space-y-2">
              {selectedCompanyTags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedCompanyTags.map((tag) => (
                    <span
                      key={tag.slug}
                      className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium"
                    >
                      {tag.name}
                      <button
                        type="button"
                        onClick={() => removeCompanyTag(tag.slug)}
                        className="text-muted-foreground hover:text-foreground ml-0.5 rounded-sm"
                        aria-label={`Remove ${tag.name}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <select
                aria-label="Add a company tag"
                value=""
                onChange={(e) => {
                  if (e.target.value) addCompanyTag(e.target.value)
                }}
                disabled={availableCompanyTags.length === 0}
                className={SELECT_CLASS}
              >
                <option value="">
                  {availableCompanyTags.length === 0
                    ? 'No more tags available'
                    : 'Add a company tag...'}
                </option>
                {availableCompanyTags.map((tag) => (
                  <option key={tag.slug} value={tag.slug}>
                    {tag.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Topics */}
          <div>
            <label className="text-foreground mb-1.5 block text-sm font-medium">
              Topics
            </label>
            <div className="space-y-2">
              {formData.topics.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {formData.topics.map((topic) => (
                    <span
                      key={topic}
                      className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium"
                    >
                      {topic}
                      <button
                        type="button"
                        onClick={() => removeTopic(topic)}
                        className="text-muted-foreground hover:text-foreground ml-0.5 rounded-sm"
                        aria-label={`Remove ${topic}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <Popover open={topicsInputFocused && hasTopicSuggestions}>
                <PopoverAnchor asChild>
                  <Input
                    value={topicsInput}
                    onChange={(e) => setTopicsInput(e.target.value)}
                    onKeyDown={handleTopicsKeyDown}
                    onFocus={() => setTopicsInputFocused(true)}
                    onBlur={handleTopicsBlur}
                    placeholder="Type topic and press Enter or comma"
                  />
                </PopoverAnchor>
                <PopoverContent
                  className="w-[--radix-popover-trigger-width] p-1"
                  align="start"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="max-h-[200px] overflow-y-auto">
                    {filteredTopics.map((topic) => (
                      <button
                        key={topic}
                        type="button"
                        className="hover:bg-accent hover:text-accent-foreground w-full rounded-sm px-2 py-1.5 text-left text-sm"
                        onClick={() => {
                          addTopic(topic)
                          setTopicsInput('')
                        }}
                      >
                        {topic}
                      </button>
                    ))}
                    {showAddNewTopic && (
                      <button
                        type="button"
                        className="text-muted-foreground hover:bg-accent hover:text-accent-foreground flex w-full items-center rounded-sm px-2 py-1.5 text-left text-sm"
                        onClick={() => {
                          addTopic(topicsInput.trim())
                          setTopicsInput('')
                        }}
                      >
                        <Plus className="mr-1.5 h-3.5 w-3.5" />
                        Add &ldquo;{topicsInput.trim()}&rdquo;
                      </button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Estimated Minutes */}
          <div>
            <label
              htmlFor="sd-estimated-minutes"
              className="text-foreground mb-1.5 block text-sm font-medium"
            >
              Estimated Minutes
            </label>
            <Input
              id="sd-estimated-minutes"
              type="number"
              value={formData.estimatedMinutes ?? ''}
              onChange={(e) => {
                const val = e.target.value
                updateField(
                  'estimatedMinutes',
                  val === '' ? null : Number.parseInt(val, 10),
                )
              }}
              placeholder="e.g. 45"
              min={0}
            />
          </div>
        </div>
      </div>

      {/* Hints section - full width below the two columns */}
      <div className="mt-8">
        <div className="mb-3 flex items-center justify-between">
          <label className="text-foreground text-sm font-medium">Hints</label>
          <Button type="button" variant="outline" size="sm" onClick={addHint}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            Add Hint
          </Button>
        </div>

        {formData.hints.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No hints yet. Click "Add Hint" to add progressive hints for
            students.
          </p>
        ) : (
          <div className="space-y-2">
            {formData.hints.map((hint, index) => (
              <div
                key={hintKeys[index]}
                draggable
                onDragStart={(e) => handleHintDragStart(e, index)}
                onDragOver={(e) => handleHintDragOver(e, index)}
                onDrop={(e) => handleHintDrop(e, index)}
                onDragEnd={handleHintDragEnd}
                className={cn(
                  'flex items-start gap-2 rounded-md border p-2 transition-colors',
                  dragIndex === index && 'opacity-50',
                  dragOverIndex === index &&
                    dragIndex !== index &&
                    'border-primary bg-primary/5',
                )}
              >
                <div className="text-muted-foreground flex cursor-grab items-center pt-1.5 active:cursor-grabbing">
                  <GripVertical className="h-4 w-4" />
                </div>
                <span className="text-muted-foreground mt-1.5 shrink-0 text-xs font-medium">
                  {index + 1}.
                </span>
                <Input
                  value={hint}
                  onChange={(e) => updateHint(index, e.target.value)}
                  placeholder={`Hint ${index + 1}`}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  onClick={() => removeHint(index)}
                  className="text-muted-foreground hover:text-destructive shrink-0"
                  aria-label={`Remove hint ${index + 1}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sources section - only show when problem has been saved */}
      {problemId && (
        <SDSourcesSection
          problemId={problemId}
          problemTitle={formData.title}
          owningSourceId={problemData?.sourceId ?? null}
          sources={linkedSources}
          onSourcesUpdate={setLinkedSources}
        />
      )}

      {/* Assertion bank - only show when problem has been saved (LG-931) */}
      {problemId && <SDAssertionsSection problemId={problemId} />}

      {/* Resources section - only show when problem has been saved */}
      {problemId && (
        <div className="mt-8">
          <div className="mb-3 flex items-center justify-between">
            <label className="text-foreground text-sm font-medium">
              Resources
            </label>
          </div>

          {/* Linked Resources List */}
          {linkedResources.length === 0 ? (
            <p className="text-muted-foreground mb-4 text-sm">
              No resources linked yet. Add resources to help students learn
              about this topic.
            </p>
          ) : (
            <div className="mb-4 space-y-2">
              {linkedResources.map((resource, index) => (
                <div
                  key={resource.id}
                  draggable
                  onDragStart={(e) => handleResourceDragStart(e, index)}
                  onDragOver={(e) => handleResourceDragOver(e, index)}
                  onDrop={(e) => handleResourceDrop(e, index)}
                  onDragEnd={handleResourceDragEnd}
                  className={cn(
                    'flex items-center gap-2 rounded-md border p-2 transition-colors',
                    resourceDragIndex === index && 'opacity-50',
                    resourceDragOverIndex === index &&
                      resourceDragIndex !== index &&
                      'border-primary bg-primary/5',
                  )}
                >
                  <div className="text-muted-foreground flex cursor-grab items-center active:cursor-grabbing">
                    <GripVertical className="h-4 w-4" />
                  </div>
                  <span
                    className={cn(
                      'shrink-0 rounded px-1.5 py-0.5 text-xs font-medium',
                      RESOURCE_TYPE_COLORS[resource.type] ||
                        'bg-gray-500/10 text-gray-600',
                    )}
                  >
                    {resource.type}
                  </span>
                  <span className="text-foreground flex-1 truncate text-sm font-medium">
                    {resource.title}
                  </span>
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-muted-foreground hover:text-foreground shrink-0"
                    title={resource.url}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setEditingResource(resource)}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                    aria-label={`Edit ${resource.title}`}
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => handleRemoveResource(resource.id)}
                    className="text-muted-foreground hover:text-destructive shrink-0"
                    aria-label={`Remove ${resource.title}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Add Resources Section */}
          {!showAddResources ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAddResources(true)}
            >
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add Resources
            </Button>
          ) : (
            <div className="border-border rounded-lg border p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-foreground text-sm font-medium">
                  Add Resources
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowAddResources(false)
                    setResourceSearchQuery('')
                    setSelectedResourceIds(new Set())
                    setShowCreateResource(false)
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>

              {/* Search existing resources */}
              <div className="relative mb-3">
                <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
                <Input
                  type="text"
                  value={resourceSearchQuery}
                  onChange={(e) => setResourceSearchQuery(e.target.value)}
                  placeholder="Search resources by title or URL..."
                  className="pl-9"
                />
              </div>

              {resourcesLoading ? (
                <div className="text-muted-foreground flex items-center gap-2 py-4 text-center text-sm">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Loading resources...
                </div>
              ) : resourceSearchQuery.trim() ? (
                filteredResources.length > 0 ? (
                  <div className="max-h-48 space-y-1 overflow-y-auto">
                    {filteredResources.map((r) => (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => handleToggleResource(r.id)}
                        className={cn(
                          'hover:bg-muted/50 flex w-full items-center gap-3 rounded-md p-2 text-left transition-colors',
                          selectedResourceIds.has(r.id) && 'bg-primary/10',
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-5 w-5 items-center justify-center rounded border',
                            selectedResourceIds.has(r.id)
                              ? 'border-primary bg-primary text-primary-foreground'
                              : 'border-muted-foreground',
                          )}
                        >
                          {selectedResourceIds.has(r.id) && (
                            <Check className="h-3 w-3" />
                          )}
                        </div>
                        <div className="flex-1 overflow-hidden">
                          <span className="text-foreground text-sm">
                            {r.title}
                          </span>
                          <div className="flex items-center gap-2">
                            <span
                              className={cn(
                                'rounded px-1 py-0.5 text-xs font-medium',
                                RESOURCE_TYPE_COLORS[r.type] ||
                                  'bg-gray-500/10 text-gray-600',
                              )}
                            >
                              {r.type}
                            </span>
                            <span className="text-muted-foreground truncate text-xs">
                              {r.url}
                            </span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground py-4 text-center text-sm">
                    No matching resources found
                  </p>
                )
              ) : (
                <p className="text-muted-foreground py-4 text-center text-sm">
                  Start typing to search resources...
                </p>
              )}

              {selectedResourceIds.size > 0 && (
                <div className="mt-3 flex justify-end">
                  <Button
                    onClick={handleAddSelectedResources}
                    disabled={isAddingResources}
                    size="sm"
                  >
                    {isAddingResources
                      ? 'Adding...'
                      : `Add ${selectedResourceIds.size} Resource(s)`}
                  </Button>
                </div>
              )}

              {/* Divider */}
              <div className="border-border my-4 border-t" />

              {/* Create New Resource Inline */}
              {!showCreateResource ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCreateResource(true)}
                  className="w-full"
                >
                  <Plus className="mr-1.5 h-3.5 w-3.5" />
                  Create New Resource
                </Button>
              ) : (
                <div className="space-y-3">
                  <h4 className="text-foreground text-sm font-medium">
                    Create New Resource
                  </h4>
                  <Input
                    value={newResourceTitle}
                    onChange={(e) => setNewResourceTitle(e.target.value)}
                    placeholder="Resource title"
                  />
                  <Input
                    value={newResourceUrl}
                    onChange={(e) => setNewResourceUrl(e.target.value)}
                    placeholder="https://..."
                  />
                  <select
                    value={newResourceType}
                    onChange={(e) => setNewResourceType(e.target.value)}
                    className={cn(
                      'border-input bg-background h-9 w-full rounded-md border px-3 text-sm shadow-xs',
                      'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                      'outline-none transition-[color,box-shadow]',
                      'dark:bg-input/30',
                    )}
                  >
                    {RESOURCE_TYPE_OPTIONS.map((type) => (
                      <option key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </option>
                    ))}
                  </select>
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setShowCreateResource(false)
                        setNewResourceTitle('')
                        setNewResourceUrl('')
                        setNewResourceType('article')
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleCreateAndLinkResource}
                      disabled={
                        isCreatingResource ||
                        !newResourceTitle.trim() ||
                        !newResourceUrl.trim()
                      }
                    >
                      {isCreatingResource ? 'Creating...' : 'Create & Link'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {editingResource && (
        <EditResourceModal
          isOpen={!!editingResource}
          onClose={() => setEditingResource(null)}
          resource={editingResource}
          onResourceUpdated={handleResourceUpdated}
        />
      )}
    </div>
  )
}
